/**
 * Payment-failed (dunning) warning: a persistent banner plus a once-per-day
 * modal, shown while the viewer's subscription is past_due (use-dunning-status).
 *
 * Contract with billing reality: Stripe keeps retrying the card through the
 * dunning window and access continues (mig 475 grace); if every retry fails
 * Stripe cancels the subscription and access is revoked by the webhook. The
 * banner is not dismissible — losing access is not snoozable news — while the
 * modal re-arms daily so it nags without imprisoning.
 *
 * CTAs: "Update payment method" → Settings → Billing (self-serve card update;
 * the next Stripe retry picks the new card up). "Pay invoice now" → the open
 * invoice's Stripe-hosted page, offered only when the billing summary is
 * readable (owner/admin — plain members see the update-payment path only).
 */
import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useDunningStatus } from '@/hooks/use-dunning-status';
import { fetchBillingSummary } from '@/api/billing';
import { formatDateWritten } from '@/lib/dates';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const MODAL_SHOWN_KEY = 'alfie:dunning-modal-shown-on';

/** Local calendar day stamp — the modal shows at most once per day. */
function todayStamp(now: Date): string {
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
}

export function shouldShowModal(lastShownStamp: string | null, now: Date): boolean {
  return lastShownStamp !== todayStamp(now);
}

function deadlineSentence(deadline: Date | null, daysLeft: number | null): string {
  if (!deadline || daysLeft === null) {
    return 'your access will be suspended soon.';
  }
  if (daysLeft <= 1) {
    return `your access will be suspended on ${formatDateWritten(deadline)} — today or tomorrow.`;
  }
  return `your access will be suspended on ${formatDateWritten(deadline)} (${daysLeft} days left).`;
}

export function DunningWarning() {
  const { data: dunning } = useDunningStatus();
  const [modalOpen, setModalOpen] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return false;
    return shouldShowModal(localStorage.getItem(MODAL_SHOWN_KEY), new Date());
  });

  // Owner/admin-only enrichment for "Pay invoice now" — the stripe-billing
  // summary 403s for plain members; fail quiet and offer update-payment only.
  const { data: summary } = useQuery({
    queryKey: ['dunning-billing-summary'],
    queryFn: fetchBillingSummary,
    enabled: !!dunning?.inDunning && modalOpen,
    staleTime: 5 * 60 * 1000,
    retry: false,
    throwOnError: false,
  });
  const openInvoiceUrl =
    summary?.invoices.find((inv) => inv.status === 'open')?.hostedInvoiceUrl ?? null;

  if (!dunning?.inDunning) return null;

  const closeModal = () => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(MODAL_SHOWN_KEY, todayStamp(new Date()));
    }
    setModalOpen(false);
  };

  return (
    <>
      <div className="mb-3 flex items-center gap-3 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
        <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground">
            Payment issue — we couldn't charge your card.
          </p>
          <p className="text-xs text-muted-foreground">
            We'll keep retrying automatically, but if payment doesn't go through,{' '}
            {deadlineSentence(dunning.deadline, dunning.daysLeft)}
          </p>
        </div>
        <Link
          to="/settings"
          search={{ tab: 'billing' }}
          className="shrink-0 rounded-lg bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground hover:opacity-90"
        >
          Update payment
        </Link>
      </div>

      <Dialog open={modalOpen} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Your payment didn't go through
            </DialogTitle>
            <DialogDescription className="pt-2 text-left">
              We tried to charge your card
              {dunning.since ? ` on ${formatDateWritten(dunning.since)}` : ' recently'}{' '}
              and the payment failed. We'll keep retrying automatically — nothing
              is lost yet — but if payment doesn't succeed,{' '}
              {deadlineSentence(dunning.deadline, dunning.daysLeft)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            {openInvoiceUrl && (
              <a
                href={openInvoiceUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-secondary"
                onClick={closeModal}
              >
                Pay invoice now
              </a>
            )}
            <Link
              to="/settings"
              search={{ tab: 'billing' }}
              onClick={closeModal}
              className="inline-flex items-center justify-center rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:opacity-90"
            >
              Update payment method
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
